import type { Failure, Setup } from "../../../../../../api/useSetups";

export const MIN_COMPARABLE_FAULTS = 2;
export const MAX_COMPARABLE_FAULTS = 3;
export const MIN_DIFFERENCE_PERCENTAGE = 0;
export const MAX_DIFFERENCE_PERCENTAGE = 100;

const MAX_QUANTITIES_PER_FAULT = 10;
const MAX_SETUP_NAME_LENGTH = 10;

export type QuantitySort = "0" | "1" | "2" | "ALPHABETICAL";
export type CollinearityLevel = "VERY_SIMILAR" | "SIMILAR" | "DISTINCT";

export interface NormalizedFault {
  failure: Failure;
  values: Map<string, number>;
}

export function clampDifferencePercentage(value: number) {
  if (!Number.isFinite(value)) return MIN_DIFFERENCE_PERCENTAGE;
  return Math.min(MAX_DIFFERENCE_PERCENTAGE, Math.max(MIN_DIFFERENCE_PERCENTAGE, value));
}

export function getFailuresByIds(setup: Setup, failureIds: string[]) {
  return failureIds.flatMap(id => {
    const failure = setup.failures.find(candidate => candidate.id === id);
    return failure ? [failure] : [];
  });
}

export function getComparableFaults(setup: Setup, failureNames: string[]) {
  return failureNames.flatMap(name => {
    const failure = setup.failures.find(candidate => candidate.name === name);
    return failure ? [failure] : [];
  });
}

export function truncateSetupName(name: string) {
  return name.length > MAX_SETUP_NAME_LENGTH ? `${name.slice(0, MAX_SETUP_NAME_LENGTH)}...` : name;
}

export function getAvailableQuantitySort(sort: QuantitySort, faultCount: number): QuantitySort {
  if (sort === "ALPHABETICAL") return sort;
  return Number(sort) < faultCount ? sort : "0";
}

export function normalizeFault(failure: Failure, setup: Setup): NormalizedFault | null {
  const sigmaByQuantityId = new Map(
    setup.diagnosisInputs
      .filter(input => input.enabled)
      .map(input => [input.quantityId, input.sigma]),
  );
  const sigmaNormalizedValues = failure.impacts.flatMap(impact => {
    const sigma = sigmaByQuantityId.get(impact.quantityId);
    return sigma && sigma > 0 ? [[impact.quantityId, impact.value / sigma] as const] : [];
  });
  const norm = Math.sqrt(sigmaNormalizedValues.reduce((sum, [, value]) => sum + value ** 2, 0));

  if (norm === 0) return null;

  return {
    failure,
    values: new Map(sigmaNormalizedValues.map(([quantityId, value]) => [quantityId, value / norm])),
  };
}

export function getCollinearityAngle(first: NormalizedFault, second: NormalizedFault) {
  const dotProduct = Array.from(first.values).reduce(
    (sum, [quantityId, value]) => sum + value * (second.values.get(quantityId) ?? 0),
    0,
  );
  const boundedDotProduct = Math.max(-1, Math.min(1, dotProduct));
  return (Math.acos(boundedDotProduct) * 180) / Math.PI;
}

export function getCollinearityPairs(faults: NormalizedFault[]) {
  const pairs: (readonly [NormalizedFault, NormalizedFault])[] =
    faults.length === MAX_COMPARABLE_FAULTS
      ? [
          [faults[0]!, faults[1]!],
          [faults[1]!, faults[2]!],
          [faults[0]!, faults[2]!],
        ]
      : faults.flatMap((first, index) =>
          faults.slice(index + 1).map(second => [first, second] as const),
        );

  return pairs.map(([first, second]) => ({
    angle: getCollinearityAngle(first, second),
    first,
    second,
  }));
}

export function getCollinearityLevel(angle: number): CollinearityLevel {
  if (angle < 5) return "VERY_SIMILAR";
  if (angle <= 20) return "SIMILAR";
  return "DISTINCT";
}

export function getRelevantQuantityIds(faults: NormalizedFault[]) {
  return new Set(
    faults.flatMap(fault =>
      Array.from(fault.values.entries())
        .sort(([, first], [, second]) => Math.abs(second) - Math.abs(first))
        .slice(0, MAX_QUANTITIES_PER_FAULT)
        .map(([quantityId]) => quantityId),
    ),
  );
}

export function meetsMinimumImpactDifference(
  quantityId: string,
  faults: NormalizedFault[],
  minimumDifferencePercentage: number,
) {
  if (minimumDifferencePercentage <= MIN_DIFFERENCE_PERCENTAGE) return true;

  const impacts = faults.map(fault => fault.values.get(quantityId) ?? 0);
  const largestDifference = Math.max(...impacts) - Math.min(...impacts);
  return largestDifference > minimumDifferencePercentage / 100;
}

export function sortQuantityIds(
  quantityIds: string[],
  faults: NormalizedFault[],
  quantityNameById: Map<string, string>,
  sort: QuantitySort,
) {
  const sortedQuantityIds = [...quantityIds];

  if (sort === "ALPHABETICAL") {
    return sortedQuantityIds.sort((first, second) =>
      (quantityNameById.get(first) ?? first).localeCompare(quantityNameById.get(second) ?? second),
    );
  }

  return sortedQuantityIds.sort(
    (first, second) =>
      Math.abs(faults[Number(sort)]?.values.get(second) ?? 0) -
      Math.abs(faults[Number(sort)]?.values.get(first) ?? 0),
  );
}
